<template>
	<div
		class="w-full bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden transition-all duration-300 animate-fade-in-up quotation-container"
	>
		<!-- Compact Header -->
		<div
			class="bg-gradient-to-br from-blue-50 to-gray-50 px-3 py-2 border-b border-gray-200"
		>
			<div class="text-center">
				<h2 class="text-lg font-bold text-gray-900 tracking-tight">
					Insurance Quotation
				</h2>
				<p class="text-xs text-gray-600">
					Review your selected product and coverage details
				</p>
			</div>
		</div>

		<div class="p-3">
			<!-- Compact Product Summary -->
			<div
				v-if="selectedProduct"
				class="mb-4 bg-gradient-to-br from-blue-50 to-gray-50 rounded-lg p-3 border border-gray-200"
			>
				<div class="flex items-center gap-2 mb-3">
					<img
						:src="
							selectedProduct.productLogo ||
							'https://api.prod.nexus.ke/static/default-logo.webp'
						"
						alt="Product Logo"
						class="w-8 h-8 object-contain rounded border border-gray-200 shadow-sm bg-white p-1"
					/>
					<div class="min-w-0 flex-1">
						<h3 class="text-sm font-bold text-gray-900 truncate">
							{{ selectedProduct.productName }}
						</h3>
						<p class="text-xs text-gray-600 truncate">
							{{ getUnderwriterName() }}
						</p>
					</div>
				</div>

				<!-- Compact Premium Display -->
				<div class="space-y-2">
					<div
						class="bg-white rounded p-3 border border-gray-200 shadow-sm"
					>
						<p
							class="text-xs font-semibold text-gray-500 uppercase mb-1"
						>
							Total Premium
						</p>
						<p class="text-lg font-bold text-blue-700">
							KES
							{{
								selectCalculatedQuote.basePremium.toLocaleString()
							}}
						</p>
					</div>

					<div
						v-if="
							vehicleValue && selectedCoverType !== 'third-party'
						"
						class="bg-white rounded p-3 border border-gray-200 shadow-sm"
					>
						<p
							class="text-xs font-semibold text-gray-500 uppercase mb-1"
						>
							Vehicle Value
						</p>
						<p class="text-sm font-semibold text-gray-800">
							KES {{ vehicleValue.toLocaleString() }}
						</p>
					</div>

					<div
						class="bg-gradient-to-r from-blue-50 to-indigo-50 rounded p-3 border border-blue-200 shadow-sm"
					>
						<p
							class="text-xs font-semibold text-blue-600 uppercase mb-1"
						>
							Cover Type
						</p>
						<p class="text-base font-bold text-blue-800 capitalize">
							{{ selectedCoverType.name }}
						</p>
					</div>
				</div>

				<!-- Compact Third Party Notice -->
				<div
					v-if="selectedCoverType === 'third-party'"
					class="mt-2 flex items-center p-2 bg-green-50 rounded border border-green-200"
				>
					<i
						class="fas fa-check-circle text-green-600 flex-shrink-0"
					></i>
					<span class="text-xs font-medium text-green-700"
						>Third Party Cover - Legal Requirement Coverage</span
					>
				</div>
			</div>

			<!-- Coverage Details Section -->
			<div class="space-y-3">
				<!-- Selected Benefits (Only for non-third-party) -->
				<div
					v-if="
						selectedProduct &&
						selectedCoverType !== 'third-party' &&
						selectedBenefits.length
					"
					class="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden"
				>
					<div class="bg-primary px-3 py-2">
						<h3
							class="text-sm font-semibold text-white flex items-center"
						>
							<i class="fas fa-check mr-2"></i>
							Selected Benefits Limits
						</h3>
					</div>
					<div class="p-3">
						<div class="space-y-2">
							<div
								v-for="benefit in selectedBenefits"
								:key="benefit.benefit.benefitSystemRef"
								class="flex items-center justify-between p-2 bg-gray-50 rounded border border-gray-200"
							>
								<div class="flex items-center min-w-0 flex-1">
									<div
										class="w-1.5 h-1.5 bg-blue-600 rounded-full mr-2 flex-shrink-0"
									></div>
									<span
										class="text-xs font-medium text-gray-700 truncate"
										>{{
											benefit.benefit
												.benefit_category_name ||
											'Benefit'
										}}</span
									>
								</div>
								<span
									class="text-xs font-bold text-blue-700 whitespace-nowrap ml-2"
									>KES {{ benefit.limit }}</span
								>
							</div>
						</div>
					</div>
				</div>

				<!-- Third Party Coverage Details -->
				<div
					v-if="selectedCoverType.code === 'TPO'"
					class="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden"
				>
					<div
						class="bg-gradient-to-r from-amber-500 to-amber-600 px-3 py-2"
					>
						<h3
							class="text-sm font-semibold text-white flex items-center"
						>
							<i class="fas fa-exclamation-triangle mr-2"></i>
							Third Party Only Coverage
						</h3>
					</div>
					<div class="p-3">
						<div
							class="bg-amber-50 rounded p-2 border border-amber-200"
						>
							<ul class="space-y-1 text-xs text-amber-800">
								<li class="flex items-start">
									<i
										class="fas fa-check text-amber-600 flex-shrink-0 mt-0.5 mr-1"
									></i>
									<span
										>Legal requirement coverage for
										third-party damages</span
									>
								</li>
								<li class="flex items-start">
									<i
										class="fas fa-times text-amber-600 flex-shrink-0 mt-0.5 mr-1"
									></i>
									<span
										>Own vehicle damage, theft, or fire not
										covered</span
									>
								</li>
								<li class="flex items-start">
									<i
										class="fas fa-info-circle text-amber-600 flex-shrink-0 mt-0.5 mr-1"
									></i>
									<span
										>Premium is fixed regardless of vehicle
										value</span
									>
								</li>
							</ul>
						</div>
					</div>
				</div>

				<!-- No Benefits Message (Only for non-third-party) -->
				<div
					v-if="
						selectedProduct &&
						selectedCoverType !== 'third-party' &&
						!selectedBenefits.length
					"
					class="bg-white rounded-lg border border-gray-200 shadow-sm p-3 text-center"
				>
					<i class="fas fa-info-circle text-gray-300 mb-2"></i>
					<h4 class="text-sm font-semibold text-gray-700 mb-1">
						Core Coverage Only
					</h4>
					<p class="text-xs text-gray-600">
						No additional benefits selected
					</p>
				</div>

				<!-- Premium Breakdown (Only for non-third-party with calculated quote) -->
				<div
					v-if="
						selectedProduct &&
						selectedCoverType.code !== 'TPO' &&
						selectCalculatedQuote
					"
					class="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden"
				>
					<!-- Header Section -->
					<div class="bg-gray-100 px-3 py-2 border-b border-gray-200">
						<h4
							class="text-sm font-semibold text-gray-900 tracking-tight"
						>
							Premium Breakdown
						</h4>
					</div>

					<!-- Product Charges Section -->
					<div class="px-3 py-2 space-y-3">
						<div>
							<h5 class="text-xs font-medium text-gray-900">
								Product Charges
							</h5>
							<div class="mt-2 space-y-2">
								<div
									v-for="charge in selectCalculatedQuote.chargesApplied"
									:key="charge.charge_name"
									class="flex items-center justify-between py-1 border-b border-gray-100 last:border-b-0"
								>
									<div class="flex-1">
										<span
											class="text-xs font-medium text-gray-600"
											>Name</span
										>
										<p class="text-xs text-gray-900">
											{{ charge.charge_name }}
										</p>
									</div>
									<div class="flex-1 text-right">
										<span
											class="text-xs font-medium text-gray-600"
											>Amount</span
										>
										<p
											class="text-xs font-semibold text-gray-900"
										>
											{{ charge.value }}
										</p>
									</div>
								</div>
							</div>
						</div>

						<!-- Total Charges -->
						<div class="flex items-center justify-between pt-2">
							<h5 class="text-xs font-medium text-gray-900">
								Total Charges
							</h5>
							<p class="text-sm font-bold text-gray-900">
								{{
									Math.round(
										selectCalculatedQuote.totalPremiumCharges,
									)
								}}
							</p>
						</div>

						<div class="flex items-center justify-between pt-2">
							<h5 class="text-xs font-medium text-gray-900">
								Additional Premium
							</h5>
							<p class="text-sm font-bold text-gray-900">
								{{
									Math.round(
										selectCalculatedQuote.totalAdditionalPremium,
									)
								}}
							</p>
						</div>
					</div>

					<!-- Total Premium Section -->
					<div class="px-3 py-2 bg-gray-50 border-t border-gray-200">
						<div
							class="flex justify-between items-center p-2 bg-blue-100 rounded"
						>
							<span class="text-xs font-medium text-gray-800"
								>Total Calculated Premium</span
							>
							<span class="text-sm font-bold text-blue-800"
								>KES
								{{
									Math.round(
										selectCalculatedQuote.totalPremium,
									)
								}}</span
							>
						</div>
					</div>
				</div>
			</div>
			<div class="mb-4 mt-4">
				<!-- Compact Header with Toggle Button -->
				<button
					class="w-full bg-white hover:bg-blue-50 rounded-lg px-3 py-2 transition-all duration-200 flex items-center justify-between text-sm font-semibold text-slate-800 cursor-pointer select-none shadow-sm border border-blue-100 hover:border-blue-200 group"
					@click="showCriteria = !showCriteria"
					type="button"
					:aria-expanded="showCriteria"
					aria-controls="criteria-content"
				>
					<div class="flex items-center gap-2">
						<div
							class="w-6 h-6 bg-blue-100 rounded flex items-center justify-center group-hover:bg-blue-200 transition-colors"
						>
							<i class="fas fa-clipboard-list text-blue-600"></i>
						</div>
						<span>View Calculation Criteria</span>
					</div>

					<!-- Chevron Icon -->
					<i
						class="fas fa-chevron-down text-blue-600 transition-transform duration-300 flex-shrink-0"
						:class="{ 'rotate-180': showCriteria }"
					></i>
				</button>

				<!-- Collapsible Content -->
				<transition name="fade-slide">
					<section
						v-if="showCriteria"
						id="criteria-content"
						class="mt-4 bg-gradient-to-br from-blue-50 to-white rounded-xl p-4 border border-blue-100 shadow-sm"
					>
						<div class="space-y-3">
							<div
								v-for="(
									logic, index
								) in selectCalculatedQuote.calculationLogic ||
								[]"
								:key="index"
								class="flex items-start gap-3 p-4 bg-white rounded-lg border border-blue-100 hover:border-blue-200 transition-colors"
							>
								<div class="flex-shrink-0 mt-0.5">
									<div
										class="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center"
									>
										<span
											class="text-xs font-bold text-blue-700"
											>{{ index + 1 }}</span
										>
									</div>
								</div>
								<p
									class="text-sm sm:text-base text-slate-700 leading-relaxed flex-1"
								>
									{{ logic }}
								</p>
							</div>

							<!-- Fallback if no logic available -->
							<div
								v-if="
									!selectCalculatedQuote.calculationLogic ||
									!selectCalculatedQuote.calculationLogic
										.length
								"
								class="flex flex-col items-center justify-center py-8 text-center"
							>
								<div
									class="w-14 h-14 sm:w-16 sm:h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4"
								>
									<i
										class="fas fa-file-alt text-slate-400"
									></i>
								</div>
								<p class="text-sm font-medium text-slate-500">
									No calculation criteria available
								</p>
								<p class="text-xs text-slate-400 mt-1">
									Criteria will appear here once processed
								</p>
							</div>
						</div>
					</section>
				</transition>
			</div>

			<!-- Compact Action Buttons -->
			<div
				class="mt-4 flex flex-col sm:flex-row sm:justify-between gap-2 actions-row"
			>
				<button
					@click="previous"
					class="order-2 sm:order-1 flex items-center justify-center px-3 py-2 text-sm font-medium text-gray-600 bg-white border-2 border-gray-300 rounded-lg hover:border-gray-400 hover:text-gray-800 focus:ring-2 focus:ring-gray-200 transition-all duration-200 compact-btn"
				>
					<i class="fas fa-arrow-left mr-2"></i>
					Back
				</button>
				<button
					@click="next"
					class="order-1 sm:order-2 flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg hover:from-blue-700 hover:to-blue-800 focus:ring-2 focus:ring-blue-200 transition-all duration-200 shadow-md hover:shadow-lg compact-btn"
				>
					<i class="fas fa-lock mr-2"></i>
					Proceed to Purchase
				</button>
			</div>
		</div>

		<!-- Compact No Product Selected State -->
		<div v-if="!selectedProduct" class="text-center p-4">
			<div class="max-w-sm mx-auto">
				<i class="fas fa-info-circle text-gray-300 mb-2"></i>
				<h3 class="text-sm font-semibold text-gray-700 mb-1">
					No Product Selected
				</h3>
				<p class="text-xs text-gray-500 mb-3">
					Please select a product to view quotation details
				</p>
				<button
					@click="$router.push({ name: 'ProductQuotation' })"
					class="px-3 py-1 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 transition-colors duration-200"
				>
					Back to Products
				</button>
			</div>
		</div>
	</div>
</template>

<script>
import { mapGetters, mapState, mapActions } from 'vuex';
import { loadState } from '../../../../../libs/persist'; // Import loadState
import { calaculateProductPremium } from '@nana-tec/insurance';
// import {numberFormatter} from '@nana-tec/utils'
export default {
	name: 'Quotation',
	data() {
		return {
			basePremiumRate: 0,
			showCriteria: false,
		};
	},

	computed: {
		...mapGetters('product', [
			'selectedProduct',
			'selectedCoverType',
			'getProductCharges',
		]),
		...mapGetters('benefits', ['selectedBenefits', 'selectedExcesses']),
		...mapGetters('quotation', ['selectCalculatedQuote', 'selectQuotationData']),
		...mapGetters('auth', ['isAuthenticated']),
		...mapState('', ['vehicleValue']),
		finalPremium() {
			if (!this.selectedProduct) return 0;

			// For third-party, fetch the saved basePremium (expected to be 3500)
			if (this.selectedCoverType.code === 'TPO') {
				const savedBasePremium = loadState('basePremium');
				return savedBasePremium !== null &&
					savedBasePremium !== undefined
					? Number(savedBasePremium)
					: 3500; // Fallback to 3500 if saved value is not available
			}

			// For other cover types, use calculated quote from store
			return this.selectCalculatedQuote &&
				this.selectCalculatedQuote.total
				? this.selectCalculatedQuote.total
				: 0;
		},

		modifiedBenefits() {
			if (
				!this.selectedProduct ||
				!this.selectedProduct.productBenefits ||
				this.selectedCoverType.code === 'TPO'
			) {
				return [];
			}

			return this.selectedBenefits.filter((selectedBenefit) => {
				const originalBenefit =
					this.selectedProduct.productBenefits.find(
						(benefit) =>
							benefit.benefitSystemRef ===
							selectedBenefit.benefit.benefitSystemRef,
					);
				if (!originalBenefit || !originalBenefit.benefitLimit)
					return true;
				return (
					selectedBenefit.limit !==
					originalBenefit.benefitLimit.defaultLimit
				);
			});
		},
	},

	methods: {
		...mapActions('insurance/motorPrivate', ['clearErrors']),
		...mapActions('auth', ['setPurchaseType']),
		...mapActions('quotation', ['setCalculatedQuotation']),

		getBenefitCalculation() {
			// 1. Early returns with clear guards
			if (!this.selectedProduct) {
				console.warn('selectedProduct is not available yet');
				return null;
			}

			if (!this.selectedProduct.basePremiumRate) {
				console.warn('basePremiumRate is missing on selectedProduct');
				return null;
			}

			  if (!this.selectedBenefits || this.selectedBenefits.length === 0) {
			    console.warn('No benefits selected');
			    return null;
			  }

			if (!this.selectQuotationData.vehicleValue) {
				console.warn('Vehicle value (sum insured) is required');
				return null;
			}
			const clientBenefits = this.selectedBenefits.map((ben) => {
				const benefitInternaleRef = ben.benefit.benefitInternaleRef;
				const limit = ben.limit;
				return { benefitInternaleRef, limit };
			});

			const benefitStruct = [...clientBenefits];

			const charges = (this.getProductCharges || []).map((charge) => ({
				charge_code: charge.charge_code || charge.chargeCode,
				chargeRate: {
					rateType: charge.charge_type || charge.rateType || 'FIXED',
					rateValue: Number(charge.value || charge.rateValue || 0),
				},
				charge_type: charge.charge_type,
				value: charge.value ||0,
				charge_name: charge.charge_name,
			}));

			const premiumDetails = {
				risk: {
					riskType: 'ASSET',
					sumInsured: Number(this.selectQuotationData.vehicleValue),
				},
				productDetails: {
					productBaseRate: {
						rateType: this.selectedProduct.basePremiumRate.rateType,
						rateValue: this.selectedProduct.basePremiumRate.value ||0,
					},
					charges,
					benefits: this.selectedProduct.productBenefits || [],
					excessConfig: this.selectedProduct.excessConfig || null,
				},
				benefits: benefitStruct,
				excessValue:this.selectedExcesses.length&& this.selectedExcesses[0].value ||0,
			};

			console.log('Premium calculation payload:', premiumDetails);

			try {
				const result = calaculateProductPremium(premiumDetails);
				this.setCalculatedQuotation(result);
				return result;
			} catch (error) {
				console.error('Error calculating premium:', error);
				Event.$emit('notifications:show', {
					type: 'error',
					title: 'Calculation Failed',
					message: 'Failed to calculate premium. Please try again.',
					duration: 5000,
				});
				return null;
			}
		},

		getUnderwriterName() {
			if (!this.selectedProduct || !this.selectedProduct.underwriter)
				return 'N/A';
			if (this.selectedProduct.underwriter.name)
				return this.selectedProduct.underwriter.name;
			if (
				this.selectedProduct.underwriter[0] &&
				this.selectedProduct.underwriter[0].name
			) {
				return this.selectedProduct.underwriter[0].name;
			}
			return 'N/A';
		},

		getIntermediaryName() {
			if (!this.selectedProduct) return 'N/A';
			return this.selectedProduct.intermediary || 'N/A';
		},
		getChargeAmount(charge) {
			if (charge.charge_type == 'Percentage') {
				let amount =
					(charge.value * this.selectedProduct.basePremium) / 100;
				return Math.round(amount);
			} else if (charge.charge_type == 'Fixed') {
				return charge.value;
			}
		},
		getTotalCharges() {
			let totalCharges = 0;
			if (!this.getProductCharges || this.getProductCharges === 0) {
				totalCharges = 0;
			} else {
				totalCharges = this.getProductCharges.reduce(
					(total, charge) => total + this.getChargeAmount(charge),
					0,
				);
			}

			const totalBenefits = this.selectedBenefits.reduce(
				(total, ben) => total + ben.limit,
				0,
			);
			return totalCharges;
		},
		getTotalPremium() {
			let premium = this.selectCalculatedQuote.totalPremium;
			let totalSum = premium + this.getTotalCharges();
			return Math.round(totalSum);
		},

		next() {
			this.clearErrors();
			this.setPurchaseType('new');

			const quotationData = {
				totalPremium: this.selectCalculatedQuote.totalPremium,
				coverType: this.selectedCoverType,
				productId:
					this.selectedProduct && this.selectedProduct.id
						? this.selectedProduct.id
						: null,
				totalCharged: this.getTotalCharges(),
				selectedBenefits:
					this.selectedCoverType.code === 'TPO'
						? []
						: this.selectedBenefits,
				// vehicleValue:
				// 	this.selectedCoverType.code === 'TPO'
				// 		? null
				// 		: this.vehicleValue,
			};

			this.$store.dispatch('quotation/setQuotationData', quotationData);

			if (!this.isAuthenticated) {
				this.$router.push('/insurance/auth');
			} else {
				this.$router.push('/motor/cover-details/vehicle-details');
			}
		},

		previous() {
			this.clearErrors();
			const previousRoute =
				this.selectedCoverType === 'third-party'
					? 'BasicCoverDetails'
					: 'BenefitsPage';
			this.$router.push({ name: previousRoute });
		},
	},

	mounted() {
		console.log('Selected Cover Type:', this.selectedCoverType);
		console.log('Selected Product:', this.selectedBenefits);
		console.log('Final Premium:', this.finalPremium);
		console.log(
			'Is Third Party:',
			this.selectedCoverType === 'third-party',
		);
		console.log('==benefitCalculation', this.getBenefitCalculation());
		console.log("==quotationData",this.selectQuotationData)
	},
};
</script>
<!-- 144.0.319.104 -->
<style scoped>
.transition-shadow {
	transition: box-shadow 0.3s ease-in-out;
}
@keyframes fade-in-up {
	from {
		opacity: 0;
		transform: translateY(20px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

.animate-fade-in-up {
	animation: fade-in-up 0.5s ease-out forwards;
}
.fade-slide-enter-active,
.fade-slide-leave-active {
	transition: all 0.3s ease;
}
.fade-slide-enter-from,
.fade-slide-leave-to {
	opacity: 0;
	max-height: 0;
	margin-top: 0;
	padding-top: 0;
	padding-bottom: 0;
}
.fade-slide-enter-to,
.fade-slide-leave-from {
	opacity: 1;
	max-height: 500px;
}

/* Improved mobile-specific styles */
@media (max-width: 640px) {
	.quotation-container {
		margin: 0.5rem;
		border-radius: 0.65rem;
		max-height: calc(100vh - 3.5rem);
		overflow: auto;
	}

	.quotation-container .p-6,
	.quotation-container .p-4,
	.quotation-container .p-3 {
		padding: 0.5rem !important;
	}

	.actions-row {
		flex-direction: column !important;
		align-items: stretch;
	}

	.compact-btn {
		padding: 0.55rem 0.75rem !important;
		font-size: 0.95rem !important;
		border-radius: 0.55rem !important;
	}
}

@media (prefers-reduced-motion: reduce) {
	.animate-fade-in-up {
		animation: none;
	}
	.transition-all {
		transition: none;
	}
}
</style>
